import React, { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import Card from "../../../newComponents/card";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import {
  ApiError,
  PaymentMethodTypesEnum,
  PaymentMethodEntityTypesEnum,
  PaymentMethodVM,
  PayDueAccountStatusEnum,
} from "@propertelligent/client-api";
import "./Subscription.css";
import { GetImages } from "../../../utils/GetImages";

// Payment form related imports
import { PaymentService } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import { UserLoginData } from "../../../components/common/charts/Enums";

// Plaid support (optional - keep even if not using yet)
import {
  PlaidAccountDetails,
} from "../../../components/firstTimeUserExperience/Context";

// API Services
import { OrganizationService } from "@propertelligent/client-api";
import EmptyState from "../../../newComponents/emptystate";
import CurrencyTooltip from "../../currencytooltip/CurrencyTooltip";
import SimpleSpinner from "../../loader";

// Permission support
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import { ManageAutoPayForm } from "../../../tenantComponent/components/ManageAutoPay";

// Auto Pay Import

// ────────────────────────────────────────────────
//                 Types 
// ────────────────────────────────────────────────
const statusClassMap: Record<number, string> = {
  [PayDueAccountStatusEnum.Due]: "status-due",
  [PayDueAccountStatusEnum.Paid]: "status-paid",
  [PayDueAccountStatusEnum.PaidPartially]: "status-partially-paid",
  [PayDueAccountStatusEnum.WaivedPartially]: "status-waived-partially",
  [PayDueAccountStatusEnum.Waived]: "status-waived",
};

const MySubscriptionComponent = () => {
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isAutoPayOpen, setIsAutoPayOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any | null>(null);
  const [currentInvoice, setCurrentInvoice] = useState<any | null>(null);
  const [pastInvoices, setPastInvoices] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));

  const check = usePermissions();
  const canWrite = check.canWrite("Subscription");

  // Form for Add Payment Method
  const methods = useForm<PaymentMethodVM>({
    defaultValues: {
      id: undefined,
      accountName: "",
      paymentMethodTypeId: PaymentMethodTypesEnum.ACH,
      entityTypeId: PaymentMethodEntityTypesEnum.Organization,
      entityId: organizationId,
      achPaymentMethod: {
        accountNumber: "",
        routingNumber: "",
      },
      creditCardPaymentMethod: {
        cardHolderName: "",
        cardNumber: "",
        cvvNumber: "",
        billingZipCode: "",
        expMonth: undefined,
        expYear: undefined,
      },
    },
    mode: "onBlur",
  });

  const { handleSubmit, watch, reset, setValue, formState: { isDirty } } = methods;
  const selectedPaymentMethodType = Number(watch("paymentMethodTypeId"));

  // Form for Auto Pay
  const autoPayFormId = "subscription-manage-auto-pay-form";
  const autoPayMethods = useForm({
    defaultValues: {
      isEnabled: "true",
      accountId: "",
      billingPeriodId: "",
      dayOfMonth: "",
    },
  });

  const fetchSubscriptionData = async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. Get subscription details
      const subResponse = await OrganizationService.getApiOrganizationSubscriptionDetails(organizationId);
      if (subResponse.success) {
        setSubscription(subResponse.success);
      }

      // 2. Get current invoice — 404 is expected for new orgs (no active invoice yet);
      // let the empty state render and keep the rest of the page working.
      try {
        const currentResponse = await OrganizationService.getApiOrganizationCurrentSubscriptionInvoice(organizationId);
        if (currentResponse.success) {
          setCurrentInvoice(currentResponse.success);
        }
      } catch (currentErr) {
        const apiError = currentErr as ApiError;
        if (apiError?.status !== 404) {
          throw currentErr;
        }
        setCurrentInvoice(null);
      }

      // 3. Get past invoices (billing history)
      const pastResponse = await OrganizationService.getApiOrganizationPastSubscriptionInvoice(organizationId);
      if (pastResponse.success && Array.isArray(pastResponse.success)) {
        setPastInvoices(pastResponse.success);
      }

      // Payment methods API call hata di gayi

    } catch (err: any) {
      console.error("Failed to load subscription data:", err);
      setError("Unable to load subscription information. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  //                Fetch Subscription Data
  // ────────────────────────────────────────────────
  useEffect(() => {
    if (!organizationId || isNaN(organizationId)) {
      setError("Organization ID not found. Please log in again.");
      setLoading(false);
      return;
    }

    fetchSubscriptionData();
  }, [organizationId]);

  // ────────────────────────────────────────────────
  //            Format Options for SelectField
  // ────────────────────────────────────────────────
  const paymentMethodOptions = paymentMethods.map((method) => ({
    Id: method.id,
    Name: `${method.accountName} (${method.formatted?.PaymentMethodTypeId})`,
  }));

  // ────────────────────────────────────────────────
  //                   Form Submits
  // ────────────────────────────────────────────────

  // Submit for Add Payment Method
  const onSubmit = async (data: PaymentMethodVM) => {
    try {
      let payload: any;

      if (selectedPaymentMethodType === PaymentMethodTypesEnum.ACH) {
        payload = {
          ...data,
          paymentMethodTypeId: PaymentMethodTypesEnum.ACH,
          entityTypeId: PaymentMethodEntityTypesEnum.Organization,
          entityId: organizationId,
          achPaymentMethod: data.achPaymentMethod,
          creditCardPaymentMethod: null,
        };
      } else if (selectedPaymentMethodType === PaymentMethodTypesEnum.CreditCard) {
        payload = {
          ...data,
          paymentMethodTypeId: PaymentMethodTypesEnum.CreditCard,
          entityTypeId: PaymentMethodEntityTypesEnum.Organization,
          entityId: organizationId,
          creditCardPaymentMethod: {
            ...data.creditCardPaymentMethod,
            cardNumber: data.creditCardPaymentMethod?.cardNumber?.replace(/\s/g, ""),
          },
          achPaymentMethod: null,
        };
      } else {
        return;
      }

      const apiCall = data.id
        ? PaymentService.putApiPaymentMethod
        : PaymentService.postApiPaymentMethod;

      await apiHelper(
        apiCall,
        {
          title: data.id ? "Payment Account Updated" : "Payment Account Added",
          successMessage: data.id
            ? "Account updated successfully"
            : "Account added successfully",
          failureMessage: data.id
            ? "Unable to update payment account"
            : "Unable to add payment account",
          showNotification: true,
          successIcon: (
            <img
              src={GetImages.toastPaymentAccountIcon}
              alt="success"
              width="20"
              height="20"
            />
          ),
        },
        payload
      );

      setAddModalOpen(false);
      reset();
      await fetchSubscriptionData();

    } catch (error) {
      console.error("Error submitting payment method:", error);
    }
  };

  // Submit for Auto Pay
  const handleSaveAutoPay = async (values: any) => {
    try {
      console.log("Saving Auto Pay settings:", values);
      // Yahan API call aayegi future mein:
      // await apiHelper(PaymentService.updateAutoPay, {}, payload);

      setIsAutoPayOpen(false);
    } catch (err) {
      console.error("Manage Auto Pay error", err);
    }
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    reset();
  };

  const handlePlaidSuccess = (plaidData: PlaidAccountDetails) => {
    setAddModalOpen(false);
    reset();
  };

  // ────────────────────────────────────────────────
  //                     RENDER
  // ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="subscription-container container-fluid min-vh-70 d-flex align-items-center justify-content-center">
        <SimpleSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-container container-fluid py-5">
        <div className="alert alert-danger text-center">{error}</div>
      </div>
    );
  }

  return (
    <PermissionGuard permission="Subscription" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className="subscription-container container-fluid">
        <div className="top-listing-header-top tenant-additional-top-listing-header-top">
          <h2 className="H2">My Subscription</h2>
          <SimpleButton label="Manage Auto Pay" onClick={() => setIsAutoPayOpen(true)} />
        </div>

        <div className="subscription-top-section">
          <div className="info-grid">
            <div className="info-card-subs">
              <div className="icon-box">
                <img
                  src={GetImages.SubscriptionTrialIcon}
                  alt="Trial Icon"
                  width={30}
                  height={30}
                  className="color-white-icon"
                />
              </div>
              <div>
                <div className="card-value">
                  {subscription?.formatted.SubscriptionType || "—"}
                </div>
                <div className="card-label">Subscription</div>
              </div>
            </div>

            <div className="info-card-subs">
              <div className="icon-box">
                <img
                  src={GetImages.subscriptionCalendarIcon}
                  alt="Calendar Icon"
                  width={24}
                  height={24}
                  className="color-white-icon"
                />
              </div>
              <div>
                <div className="card-value">
                  {subscription?.formatted.ExpirationDateDateString || "—"}
                </div>
                <div className="card-label">Expires on</div>
              </div>
            </div>

            <div className="info-card-subs">
              <div className="icon-box">
                <img
                  src={GetImages.subscriptionCalendarIcon}
                  alt="Calendar Icon"
                  width={24}
                  height={24}
                  className="color-white-icon"
                />
              </div>
              <div>
                <div className="card-value">
                  {subscription?.formatted.BillingCycle || "—"}
                </div>
                <div className="card-label">Billing Cycle</div>
              </div>
            </div>

            <div className="info-card-subs">
              <div className="icon-box">
                <img
                  src={GetImages.subscriptionCalendarIcon2}
                  alt="Calendar Icon"
                  width={24}
                  height={24}
                  className="color-white-icon"
                />
              </div>
              <div>
                <div className="card-value">
                  {subscription?.autoRenewal ? "On" : "Off"}
                </div>
                <div className="card-label">Auto-Renewal Status</div>
              </div>
            </div>
          </div>

          <Card className="current-invoice-card">
            <div className="invoice-header">
              <span className="section-subtitle">Current Invoice</span>
              <span
                className={
                  "status-badge " +
                  (statusClassMap[currentInvoice?.paymentDueAccountStatusId ?? -1] ??
                    "status-pending")
                }
              >
                {currentInvoice?.formatted.PaymentDueAccountStatusId || "Unknown"}
              </span>
            </div>

            {currentInvoice ? (
              <>
                <div className="invoice-content-row">
                  <span className="due-date-label">
                    Due Date: {currentInvoice.formatted.DueDateDateString}
                  </span>
                </div>

                <div className="invoice-row amount-row">
                  <span className="label">Amount Due</span>
                  <CurrencyTooltip
                    content={currentInvoice?.formatted?.AmountRaw}
                    formatted={currentInvoice?.formatted?.Amout}
                  >
                    <span className="total-amount">
                      {currentInvoice?.formatted?.Amount}
                    </span>
                  </CurrencyTooltip>
                </div>

                <div className="invoice-row">
                  <span className="label">Invoice Number</span>
                  <span className="inv-num">
                    # {currentInvoice.referenceNumber || "---"}
                  </span>
                </div>
              </>
            ) : (
              <EmptyState heading="No Active Invoice" bodytext="There is no active invoice for your subscription."
                icon={GetImages.emptyStateData} />
            )}
          </Card>
        </div>

        {/* Payment Method Card completely hata diya */}

        <Card className="billing-history-card">
          <div className="table-header">
            <h5>Billing History</h5>
          </div>
          <div className="table-responsive">
            {pastInvoices.length > 0 ? (
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>Invoice no.</th>
                    <th>Billing Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pastInvoices.map((item, idx) => (
                    <tr key={idx}>
                      <td>#{item.referenceNumber || "---"}</td>
                      <td>{item.formatted?.DueDateDateString || "—"}</td>
                      <td>
                        <CurrencyTooltip
                          content={item?.formatted?.AmountRaw}
                          formatted={item?.formatted?.Amout}
                        >
                          <span className="">
                            {item?.formatted?.Amount}
                          </span>
                        </CurrencyTooltip></td>
                      <td>
                        <span
                          className={
                            "status-badge " +
                            (statusClassMap[item?.paymentDueAccountStatusId ?? -1] ??
                              "status-pending")
                          }
                        >
                          {item.formatted?.PaymentDueAccountStatusId || "Unknown"}
                        </span>
                      </td>
                      <td>
                        <button className="download-btn">Download</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyState heading="No past invoices found" bodytext="There are no past invoices available for your subscription."
                icon={GetImages.emptyStateData} />
            )}
          </div>
        </Card>

        {/* Add Payment Method Modal ko bhi hata diya */}
        
        {/* Manage Auto Pay Modal */}
        <RightSideModal
          isOpen={isAutoPayOpen}
          onClose={() => setIsAutoPayOpen(false)}
          title="Manage Auto Pay"
          buttonlabel="Save"
          formId={autoPayFormId}
          maxWidth={480}
          isDirty={autoPayMethods.formState.isDirty}
        >
          <FormProvider {...autoPayMethods}>
            <ManageAutoPayForm
              formId={autoPayFormId}
              onSubmit={handleSaveAutoPay}
              accountOptions={paymentMethodOptions}
            />
          </FormProvider>
        </RightSideModal>

      </div>
    </PermissionGuard>
  );
};

export default MySubscriptionComponent;